import { useState } from "react";
import type { GraphSite } from "../../../email/services/graph";
import { useFolderBrowser } from "../../onedrive/hooks/useFolderBrowser";
import { FolderBrowser } from "../../components/FolderBrowser";
import { useSharePointSites } from "../hooks/useSharePointSites";
import styles from "./SharePointPanel.module.css";

function getSiteInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "SP";
  return trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .substring(0, 2)
    .toUpperCase();
}

function getSiteName(site: GraphSite): string {
  return site.displayName || site.name || "Untitled site";
}

interface SharePointPanelProps {
  readonly graphToken: string | null;
}

export function SharePointPanel({ graphToken }: Readonly<SharePointPanelProps>) {
  const [selectedSite, setSelectedSite] = useState<GraphSite | null>(null);

  // Sites query is idle once a site is selected — we already have the list.
  const sitesQuery = useSharePointSites(selectedSite ? null : graphToken);

  // Folder browser is idle until a site is selected.
  const drivePath = selectedSite ? `/sites/${selectedSite.id}/drive` : null;
  const browser = useFolderBrowser(
    selectedSite ? graphToken : null,
    drivePath,
    selectedSite ? getSiteName(selectedSite) : "SharePoint",
  );

  if (!graphToken) {
    return <div className={styles.empty}>Sign in to access SharePoint sites.</div>;
  }

  if (selectedSite) {
    return (
      <FolderBrowser
        items={browser.items}
        loading={browser.loading}
        error={browser.error}
        path={browser.path}
        emptyMessage="This library is empty."
        onBreadcrumbClick={browser.navigateTo}
        onFolderClick={browser.navigateInto}
        extraCrumbs={[
          { label: "SharePoint", onClick: () => setSelectedSite(null) },
        ]}
      />
    );
  }

  return (
    <div className={styles.panel}>
      {sitesQuery.error && <div className={styles.error}>{sitesQuery.error}</div>}

      {sitesQuery.loading && <div className={styles.empty}>Loading sites…</div>}

      {!sitesQuery.loading && !sitesQuery.error && sitesQuery.sites.length === 0 && (
        <div className={styles.empty}>No SharePoint sites found.</div>
      )}

      {sitesQuery.sites.map((site) => {
        const name = getSiteName(site);
        const desc = site.description ?? "";
        return (
          <button
            key={site.id}
            type="button"
            className={styles.site}
            onClick={() => setSelectedSite(site)}
          >
            <div className={styles.siteIcon}>{getSiteInitials(name)}</div>
            <div className={styles.siteBody}>
              <div className={styles.siteName}>{name}</div>
              {desc && <div className={styles.siteDesc}>{desc}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
